import { Col } from '@opendoor/atoms/box';
import type { Badge, ListingState, PropertyDetailsFragment } from '__generated__/athena/graphql';
import { Pill } from 'components/marketplace/shared/Pill';

interface PropertyStatusProps {
  property: Pick<PropertyDetailsFragment, 'displayBadge' | 'listingState'>;
}

// Indexed by both `listingState` (ListingState) and `displayBadge` (Badge), so
// the key type spans both enums. `listingState` covers the states Athena
// surfaces on the PDP (AVAILABLE_SOON, IN_CONTRACT, RECENTLY_SOLD,
// ON_THE_MARKET, UNKNOWN) but omits Badge-only states like TEMP_UNAVAILABLE, so
// `getStatusLabel` falls back to `displayBadge` for the label.
const DisplayStateStatusMap: Record<Badge | ListingState, string | undefined> = {
  TEMP_UNAVAILABLE: 'Temporarily unavailable',
  AVAILABLE_SOON: 'Available soon',
  IN_CONTRACT: 'In contract',
  RECENTLY_SOLD: 'Sold',
  ON_THE_MARKET: undefined,
  IS_EXCLUSIVE: undefined,
  TRADITIONAL: undefined,
  UNKNOWN: undefined,
};

const getStatusLabel = (
  property: Pick<PropertyDetailsFragment, 'displayBadge' | 'listingState'>,
): string | undefined => {
  if (property.listingState && DisplayStateStatusMap[property.listingState]) {
    return DisplayStateStatusMap[property.listingState];
  }
  if (property.displayBadge && DisplayStateStatusMap[property.displayBadge]) {
    return DisplayStateStatusMap[property.displayBadge];
  }
  return undefined;
};

export const PropertyStatus = ({ property }: PropertyStatusProps) => {
  const label = getStatusLabel(property);
  if (!label) {
    return null;
  }
  return (
    <Col className="pb-6">
      <Pill label={label} rounded responsive />
    </Col>
  );
};
